export const SESSION_MANAGEMENT_GUIDE = `
# Session Management Guide

This guide explains how to organize, access, replay, share, and manage your VoiceThera sessions effectively.

## Understanding Your Session Library

Every personalized session you generate is automatically saved to your Session Library, giving you:

- **Unlimited replays** - Listen as many times as you want, no additional cost
- **Permanent access** - Sessions never expire or disappear
- **Organization tools** - Favorites, categories, search, and filtering
- **Sharing capabilities** - Share powerful sessions with others
- **History tracking** - See your wellness journey over time

## Session History Page

Access your complete session library:

1. Click **"Session History"** in main navigation
2. View all your generated sessions in reverse chronological order
3. Use filters and search to find specific sessions

### Session Card Information

Each session displays:

- **Session Title** - Auto-generated based on content
- **Category** - Meditation, Anxiety, Sleep, etc.
- **Duration** - Length in minutes
- **Date Generated** - When you created it
- **Favorite Star** - Quick favorite toggle
- **Play Button** - Instant replay
- **Options Menu** - Additional actions (share, download, etc.)

## Playing Sessions

### Inline Playback

**From Session History:**
1. Click the **Play** button on any session card
2. Audio player appears at bottom of screen
3. Player remains visible as you browse
4. Playback controls: Play/Pause, Seek, Volume
5. Close player anytime without losing session

**From Categories:**
- Sample sessions play immediately
- Your personalized sessions appear in "Recent Sessions" section

### Session Detail View

**For deeper engagement:**
1. Click on session title (not play button)
2. Opens full session detail page showing:
   - Full session title and description
   - Complete metadata (category, duration, date, voice, music)
   - Transcript (if available)
   - Player with full controls
   - Sharing and management options

**Detail View Features:**
- **Larger player** - Better controls and visibility
- **Read along** - Follow transcript while listening
- **Session notes** - Your personal notes about the session
- **Related sessions** - Similar sessions you've generated

## Organizing Sessions

### Favorites

Mark powerful sessions for quick access:

**To Favorite:**
1. Click the **star icon** on any session card
2. Star turns solid/gold
3. Session appears in "Favorites" filter

**Why favorite sessions:**
- Quick access to most impactful content
- Create a personal "greatest hits" library
- Identify patterns in what works for you
- Easy sharing of best sessions with friends

**Pro Tip:** Favorite sessions immediately after listening while the impact is fresh. Add a note about why it resonated.

### Filtering Sessions

**Filter by Category:**
1. Click **"Filter"** dropdown in Session History
2. Select category (Meditation, Anxiety, Sleep, etc.)
3. View only sessions from that category
4. Clear filter to see all sessions again

**Filter by Date Range:**
1. Click **"Date Range"** filter
2. Select timeframe (Last 7 days, Last month, Last year, Custom range)
3. View sessions from selected period

**Filter by Favorites:**
1. Toggle **"Favorites Only"** switch
2. View only starred sessions
3. Toggle off to see all sessions

### Searching Sessions

**Search by Content:**
1. Use search bar at top of Session History
2. Type keywords related to:
   - Session content ("work stress", "relationship conflict")
   - People mentioned ("Sarah", "my boss")
   - Situations ("presentation", "insomnia")
   - Emotions ("anxiety", "motivation")
3. Results update in real-time as you type

**Search Tips:**
- Use specific keywords from your biography
- Search by problem ("burnout") or solution ("confidence")
- Search by category names
- Search by time references ("morning", "bedtime")

### Session Notes

Add personal notes to remember context:

**Adding Notes:**
1. Open session detail view
2. Scroll to **"Session Notes"** section
3. Click **"Add Note"**
4. Write your thoughts:
   - Why you generated this session
   - What was happening in your life
   - Key insights or breakthroughs
   - When to replay this session
5. Save note

**Example Notes:**
- "Generated during Q4 work stress - incredibly helpful for perspective"
- "Listen to this before important presentations"
- "The part about Jake's support really moved me"
- "Best sleep session I've made - use regularly"

### Sorting Sessions

**Sort options:**
- **Newest First** (default) - Most recently generated
- **Oldest First** - Chronological from beginning
- **Most Played** - Sessions you've replayed most
- **Longest Duration** - Longest sessions first
- **Shortest Duration** - Quick sessions first
- **Alphabetical** - By session title

## Sharing Sessions

Share powerful sessions with friends, family, or social media:

### Share Button

1. Open session detail view
2. Click **"Share"** button
3. Choose sharing method:

**Option 1: Copy Link**
- Generates unique shareable URL
- Anyone with link can listen (no account required)
- Link never expires
- Recipient cannot see your other sessions or account

**Option 2: Email**
- Enter recipient email address
- Add optional personal message
- VoiceThera sends email with embedded player
- Recipient listens directly from email (no account needed)

**Option 3: Social Media**
- Auto-generates share text
- Includes session title and duration
- Posts direct link to session
- Privacy: Only session content is visible, not your personal info

### What Gets Shared

**Recipients can:**
- Listen to the shared session
- See session title and category
- See duration and basic metadata

**Recipients cannot:**
- Access your biography or personal information
- See your other sessions
- See who generated the session (unless you tell them)
- Edit or download the session (unless you enable download)

### Privacy Settings for Shared Sessions

**When sharing, you can:**
- Enable/disable downloads
- Set expiration date (link expires after X days)
- Track view count (see how many times link was accessed)
- Revoke access (deactivate link anytime)

**To modify share settings:**
1. Go to session detail view
2. Click **"Manage Sharing"**
3. Adjust settings
4. Save changes

### Public Library Contributions

**Share with entire VoiceThera community:**

1. Open session detail view
2. Click **"Share to Public Library"**
3. Review session before submission:
   - Ensure no private information in content
   - Verify session is high-quality and helpful
4. Add optional tags (keywords for discoverability)
5. Click **"Submit to Public Library"**

**What happens:**
- Session appears in Public Library for all users
- Your personal information remains private
- Other users can listen, favorite, and thank you
- You can remove from Public Library anytime

**Why share to Public Library:**
- Help others struggling with similar challenges
- Contribute to community wellness
- Inspire others with powerful content
- Build karma (sharing unlocks bonus features)

## Public Library (Browsing Community Sessions)

Discover sessions shared by other VoiceThera users:

### Accessing Public Library

1. Click **"Public Library"** in main navigation
2. Browse community-shared sessions
3. Filter by category, popularity, or date
4. Listen to any session for free (no credits required)

### Public Library Features

**Listen to Community Sessions:**
- All Public Library sessions are **free**
- No credits required to listen
- Unlimited access to shared content
- High-quality, community-vetted sessions

**Favorites and Collections:**
- Favorite Public Library sessions for quick access
- Create collections of helpful community sessions
- Community favorites appear in "Trending" section

**Thank the Creator:**
- Click **"Thank"** button on helpful sessions
- Anonymous appreciation to session creator
- Helps surface best content for community

### Public Library vs Personal Sessions

**Public Library Sessions:**
- Free to access (no credits)
- Not personalized to your biography
- Shared by other users (diverse perspectives)
- Great for discovering new approaches

**Your Personal Sessions:**
- Generated specifically for you
- Use your biography and current context
- Cost credits to generate (free to replay)
- Deeply personalized therapeutic content

**Best Practice:** Use Public Library for inspiration and quick support, use personalized sessions for deep therapeutic work.

## Downloading Sessions

**Coming Soon:** Offline download capability for premium users.

**When available, you'll be able to:**
- Download sessions as MP3 files
- Listen offline (flights, areas without connectivity)
- Transfer to other devices
- Keep permanent local copies

## Session Analytics

Track your wellness journey:

### Usage Statistics

View your session usage data:

1. Navigate to **Profile → Statistics**
2. See analytics:
   - Total sessions generated
   - Total listening time
   - Most-used categories
   - Credits consumed
   - Session creation trends over time

### Insights from Analytics

**What the data reveals:**
- **Category patterns** - Which categories you rely on most
- **Time patterns** - When you need support (morning/evening/specific days)
- **Frequency** - Consistency of your wellness practice
- **Evolution** - How your needs change over time

**Use insights to:**
- Identify category preferences
- Notice stress patterns (increase in anxiety sessions = stress period)
- Track wellness journey progress
- Optimize credit purchases based on usage

## Session Lifecycle Management

### Archiving Sessions

**Move old sessions out of main library:**

1. Open session detail view
2. Click **"Archive Session"**
3. Confirm archiving

**Archived sessions:**
- Removed from main Session History view
- Still accessible in "Archived Sessions" section
- Can be unarchived anytime
- Not deleted - fully recoverable

**When to archive:**
- Sessions from past life phases no longer relevant
- Cleared a challenge and don't need reminder sessions
- Decluttering session library
- Keeping only currently useful sessions visible

### Deleting Sessions

**Permanently remove sessions:**

1. Open session detail view
2. Click **"Delete Session"**
3. Confirm deletion (cannot be undone)

**When to delete:**
- Session quality was poor
- Contains outdated or inaccurate information
- No longer serves any purpose
- Privacy concerns about content

**Warning:** Deletion is permanent. Consider archiving instead if you might want access later.

## Best Practices for Session Management

### 1. Favorite Immediately After Listening

While the impact is fresh, favorite sessions that moved you. Add a quick note about why.

### 2. Create Category Playlists

Use favorites to create informal "playlists":
- Morning Motivation collection
- Bedtime Sleep collection
- Anxiety Emergency collection
- Confidence Booster collection

**How:** Favorite sessions by theme, use notes to label collections.

### 3. Regular Library Maintenance

**Monthly review:**
- Archive sessions from old life phases
- Update session notes with new insights
- Share powerful sessions with Public Library
- Identify patterns in your usage

### 4. Session Naming

Add descriptive notes to help future you:
- "Best anxiety session for work stress"
- "Use before presentations"
- "Insomnia relief - incredibly effective"
- "The one that made me cry (in a good way)"

### 5. Track Your Favorites

Notice which sessions you replay most - these reveal:
- What therapeutic approaches work for you
- Which challenges are ongoing vs resolved
- Patterns in your wellness needs

### 6. Share Strategically

**Share sessions when:**
- Someone you care about is experiencing similar challenges
- You want to introduce someone to VoiceThera
- A session perfectly articulates something you're feeling
- You've overcome something and want to help others

### 7. Use Public Library for Variety

Even with personalized sessions, browse Public Library:
- Different voices and approaches
- Perspectives you hadn't considered
- Quick support when you're out of credits
- Inspiration for what to generate next

## Troubleshooting

### "Session won't play"

**Possible causes:**
- Internet connectivity issue
- Browser audio permissions
- Audio file loading error

**Fixes:**
1. Check internet connection
2. Refresh the page
3. Try different browser
4. Check browser audio permissions
5. Contact support if persists

### "Can't find a session I generated"

**Possible causes:**
- Applied filter hiding the session
- Archived accidentally
- Search query too specific

**Fixes:**
1. Clear all filters
2. Check "Archived Sessions"
3. Broaden search terms
4. Sort by "Oldest First" and manually browse
5. Contact support with approximate date generated

### "Shared link not working"

**Possible causes:**
- Link expired (if expiration was set)
- Session deleted after sharing
- Sharing was revoked

**Fixes:**
1. Check session still exists in your library
2. Check share settings (expiration, revoked)
3. Generate new share link
4. Contact support if link should work but doesn't

### "Session transcript incorrect"

Transcripts are AI-generated and may contain minor errors.

**To report transcript issues:**
1. Open session detail view
2. Click **"Report Transcript Issue"**
3. Describe the error
4. We'll review and correct

---

## Power User Tips

### 1. Session Versioning

If a category/context pair works well, generate multiple versions:
- Save 3-4 anxiety sessions for "work presentation stress"
- Rotate through versions for variety
- Compare which version resonates most

### 2. Session Tagging System

Use notes as tags:
- "TAG: Morning"
- "TAG: Work Stress"
- "TAG: Quick (under 3 min)"
- "TAG: Deep Work"

Search by tag to find themed sessions.

### 3. Session Journal

Keep a journal tracking:
- Which sessions you used
- Context of when you listened
- Impact and insights
- Patterns over time

Use session notes or external journal app.

### 4. Pre-Generate for Known Situations

Anticipate needs and generate in advance:
- Generate "pre-flight anxiety" before travel season
- Generate "holiday family stress" before holidays
- Generate "end of project burnout" before big deadlines

Have sessions ready when you need them.

### 5. Share with Accountability Partners

Share relevant sessions with:
- Therapist (complement professional therapy)
- Spouse/partner (shared understanding of your work)
- Close friend (mutual support)
- Mentor/coach (aligned with your growth)

---

**Master your session library to maximize your wellness practice.**

Need help? Browse other guides in the Help Center or contact support at support@voicethera.com
`;
